import React, { useEffect, useState } from 'react';
import Api from "../../services/Api";
import ProgressCircle from "../../components/ProgressCircle";
import Profile from "../../components/Profile/Profile"
import StepTwo from "./components/StepTwo";
import StepThree from "./components/StepThree";
import StepFour from "./components/StepFour";
import StepFive from "./components/StepFive";
import StepSix from "./components/StepSix";
import StepSeven from "./components/StepSeven";
import {ClientProfile} from "../../interfaces/common";

const MatchWithCoach: React.FC = () => {
  const api = Api.getInstance();
  const [loaded, setLoaded] = useState(false);
  const [coachLanguages, setCoachLanguages] = useState<Array<string>>([]);

  const [clientProfile, setClientProfile] = useState<ClientProfile>({
    id: 0,
    inCompanySince: 0,
    currentRoleSince: 0,
    title: "",
    roleDescription: "",
    aboutMe: "",
    leadershipLevel: "",
    languages: "",
    coachingExperience: -1,
    coachId: "0",
    languagesNative: [],
    languagesProfessional: [],
    coachingAreas: [],
    coachIndustryExperiences: [],
    picture: "",
  });

  const [step, setStep] = useState(0);
  const [stepsDone, setStepsDone] = useState<boolean[]>([false, false, false, false, false, false]);

  /**
   * STEP RULES:
   *
   * - isOptional: If true, that step can be "skipped" even if data is missing.
   * - check: A function that returns true if the user has filled out enough data
   *          to consider the step "complete."
   *
   * The array index matches your step order:
   *    step = 0 -> StepOne
   *    step = 1 -> StepTwo
   *    step = 2 -> StepThree
   *    step = 3 -> StepFour
   *    step = 4 -> StepFive
   *    step = 5 -> StepSix
   *    step = 6 -> StepSeven (select coach)
   */
  const stepRules = [
    {
      isOptional: false,
      check: (profile: ClientProfile) => profile.currentRoleSince !== 0
    },
    {
      isOptional: false,
      check: (profile: ClientProfile) => profile.coachingExperience >= 0
    },
    {
      // Example: StepThree is optional—maybe the user can skip language selection
      // if it’s not critical to the overall process
      isOptional: false,
      check: (profile: ClientProfile) =>
        profile.languagesProfessional.length > 0 || profile.languagesNative.length > 0
    },
    {
      isOptional: false,
      check: (profile: ClientProfile) => profile.coachingAreas.length > 0
    },
    {
      isOptional: false,
      check: (profile: ClientProfile) => profile.coachIndustryExperiences.length > 0
    },
    // StepSeven may be a final confirmation step or coach selection, typically also required.
    // But if you wanted it optional for some reason, set isOptional: true, etc.
    {
      isOptional: false,
      check: (_profile: ClientProfile) => true // We'll let the code below handle final step logic
    }
  ];

  useEffect(() => {
    getCoachLanguages();
    getData();
  }, []);

  const getCoachLanguages = () => {
    api.get('client/coach_languages').then(response => {
      setCoachLanguages(response.json.languages)
    });
  }

  const getData = () => {
    api.get('client/account').then(response => {
      if (response.json.profile) {
        setClientProfile(response.json.profile);
        calculateStep(response.json.profile);
      } else {
        console.log('No title, adding new profile')
      }
      setLoaded(true);
    });
  }

  const saveProfile = (profile: ClientProfile) => {
    api.post('client/account/set', profile).then(() => {
      getData();
    });
  }

  const calculateStep = (profile: ClientProfile) => {
    let nextStep = 0;

    for (let i = 0; i < stepRules.length; i++) {
      const { isOptional, check } = stepRules[i];
      const isFilled = check(profile);

      // If a step is required (not optional) and is not filled => set nextStep
      if (!isFilled && !isOptional) {
        nextStep = i;
        break;
      }
      // Otherwise, the step is effectively "complete"
      nextStep = i + 1;
    }

    // If nextStep exceeds the last index, user is effectively done
    // (You can adjust logic if you want them to land on StepSeven.)
    if (nextStep >= stepRules.length) {
      nextStep = stepRules.length - 1;
    }

    setStep(nextStep);

    // Mark all steps < nextStep as done
    const newStepsDone = stepRules.map((_, idx) => idx < nextStep);
    setStepsDone(newStepsDone);
  }

  const changeStep = (newStep: number) => {
    // Only allow jumping forward if the previous step is done
    if (newStep === 0 || stepsDone[newStep - 1]) {
      setStep(newStep);
    } else {
      // Hack - if step 5 is done, and we want to go to step 6 (select coach), allow that
      if (newStep === 6 && stepsDone[4]) {
        setStep(6);
      }
    }
  }

  const goBackAStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  }

  const selectStep = () => {
    // Adjust to reflect your final step indexing
    // If your final step is index 5 (StepSeven):
    if (step === 6) {
      return <StepSeven profile={clientProfile}/>;
    } else if (step === 5) {
      return <StepSix profile={clientProfile} changeStep={changeStep}/>;
    } else if (step === 4) {
      return <StepFive profile={clientProfile} save={saveProfile} goBack={goBackAStep} />;
    } else if (step === 3) {
      return <StepFour profile={clientProfile} save={saveProfile} goBack={goBackAStep} />;
    } else if (step === 2) {
      return <StepThree profile={clientProfile} save={saveProfile} goBack={goBackAStep} languages={coachLanguages}/>;
    } else if (step === 1) {
      return <StepTwo profile={clientProfile} save={saveProfile} goBack={goBackAStep} />;
    } else {
      return <Profile profile={clientProfile} save={saveProfile} standalone={false} customerFiles={[]}/>;
    }
  }

  if (!loaded) {
    return (
      <div className="mt-4">
        Loading...
      </div>
    );
  }

  return (
    <div className="main-content">
      {
        step < 6 &&
          <div className="mx-16 mt-4">
              <ProgressCircle step={step} stepsDone={stepsDone} changeStep={changeStep}/>
          </div>
      }

      <div className="mt-6">
        {selectStep()}
      </div>
    </div>
  );
}

export default MatchWithCoach;
